import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useSession } from '../context/SessionContext';
import PageHeader from '../lib/ui/PageHeader';
import Button from '../lib/ui/Button';
import { ErrorBanner, EmptyState, Spinner } from '../lib/ui/Feedback';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const FIELDS = [
  ['label', 'Label (Home / Work)'],
  ['name', 'Contact name *'],
  ['phone_contact', 'Contact phone *'],
  ['house_no', 'Flat / House no. *'],
  ['line1', 'Street / Area'],
  ['landmark', 'Landmark'],
  ['city', 'City *'],
  ['state', 'State *'],
  ['pincode', 'Pincode *'],
];

export default function AddressesPage() {
  const { lang } = useLang();
  const te = lang === 'te';
  const { sessionUser } = useSession();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | address-shaped object
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => (sessionUser?.token ? { Authorization: `Bearer ${sessionUser.token}` } : {});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/me/addresses`, { headers: authHeaders(), params: { phone: sessionUser.phone } });
      setAddresses(res.data.addresses || []);
    } catch {
      try {
        const r2 = await axios.get(`${API}/api/orders/addresses/${encodeURIComponent(sessionUser.phone)}`);
        setAddresses(r2.data.addresses || []);
      } catch { setAddresses([]); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!sessionUser) { navigate('/signup', { replace: true }); return; }
    load();
  }, [sessionUser]);

  if (!sessionUser) return null;

  const emptyAddr = () => ({
    label: 'Home', name: sessionUser.name || '', phone_contact: sessionUser.phone || '',
    house_no: '', line1: '', landmark: '', city: '', state: '', pincode: '', lat: '', lng: '',
    is_default: addresses.length === 0,
  });

  const save = async () => {
    const phoneDigits = String(form.phone_contact || '').replace(/\D/g, '');
    const pin = String(form.pincode || '').replace(/\D/g, '');
    if (!form.name?.trim()) { setError('Full name is required'); return; }
    if (phoneDigits.length < 10) { setError('Valid 10-digit mobile required'); return; }
    if (!form.house_no?.trim() && !form.line1?.trim()) { setError('House no. or street address required'); return; }
    if (!form.city?.trim()) { setError('City is required'); return; }
    if (!form.state?.trim()) { setError('State is required'); return; }
    if (pin.length !== 6) { setError('Valid 6-digit pincode required'); return; }
    setSaving(true); setError('');
    const body = {
      label: form.label || 'Home',
      name: form.name.trim(),
      phone_contact: phoneDigits.slice(-10),
      house_no: form.house_no?.trim() || '',
      line1: form.line1?.trim() || '',
      landmark: form.landmark?.trim() || '',
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: pin,
      lat: form.lat !== '' && form.lat != null ? parseFloat(form.lat) : null,
      lng: form.lng !== '' && form.lng != null ? parseFloat(form.lng) : null,
      is_default: !!form.is_default,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/api/me/addresses/${encodeURIComponent(form.id)}`, { ...body, phone: sessionUser.phone }, { headers: authHeaders(), params: { phone: sessionUser.phone } });
      } else {
        await axios.post(`${API}/api/me/addresses`, { ...body, phone: sessionUser.phone }, { headers: authHeaders(), params: { phone: sessionUser.phone } });
      }
      setForm(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save address');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/api/me/addresses/${encodeURIComponent(id)}`, { headers: authHeaders(), params: { phone: sessionUser.phone } });
      await load();
    } catch (e) { setError(e.response?.data?.error || 'Could not delete'); }
  };

  const setDefault = async (id) => {
    try {
      await axios.post(`${API}/api/me/addresses/${encodeURIComponent(id)}/default`, {}, { headers: authHeaders(), params: { phone: sessionUser.phone } });
      await load();
    } catch (e) { setError(e.response?.data?.error || 'Could not set default'); }
  };

  const useGps = () => {
    if (!navigator.geolocation || !form) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })),
      () => setError('Could not get GPS location'),
      { timeout: 10000, enableHighAccuracy: true },
    );
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={te ? 'చిరునామాలు' : 'Addresses'} fallback="/profile" />
      <div className="px-4 md:px-6 py-4">
        <ErrorBanner>{error}</ErrorBanner>

        {form ? (
          <div className="bg-brand-light/40 border border-brand-light rounded-2xl p-4 mb-4">
            <div className="font-extrabold text-sm text-brand-dark mb-3">{form.id ? 'Edit address' : 'Add address'}</div>
            {FIELDS.map(([key, placeholder]) => (
              <input
                key={key}
                placeholder={placeholder}
                value={form[key] || ''}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="w-full px-3 py-2.5 border-[1.5px] border-black/10 rounded-lg text-sm mb-2 outline-none focus:border-brand"
              />
            ))}
            <div className="flex gap-2 mb-2">
              <input placeholder="Latitude" type="number" step="any" value={form.lat ?? ''} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} className="flex-1 px-3 py-2.5 border-[1.5px] border-black/10 rounded-lg text-sm outline-none" />
              <input placeholder="Longitude" type="number" step="any" value={form.lng ?? ''} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} className="flex-1 px-3 py-2.5 border-[1.5px] border-black/10 rounded-lg text-sm outline-none" />
            </div>
            <Button variant="blue" fullWidth className="mb-2.5" onClick={useGps}>📡 Use GPS</Button>
            <label className="flex items-center gap-2 text-[13px] font-bold text-ink-soft mb-3 cursor-pointer">
              <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} className="accent-brand" />
              Set as default
            </label>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => { setForm(null); setError(''); }}>Cancel</Button>
              <Button variant="accent" className="flex-[2]" loading={saving} onClick={save}>💾 Save</Button>
            </div>
          </div>
        ) : (
          <Button variant="accent" fullWidth className="mb-4" onClick={() => setForm(emptyAddr())}>➕ Add address</Button>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : addresses.length === 0 && !form ? (
          <EmptyState icon="📍" title={te ? 'చిరునామాలు లేవు' : 'No saved addresses yet'} sub={te ? 'వేగవంతమైన చెక్అవుట్ కోసం చిరునామా జోడించండి.' : 'Add a delivery address for faster checkout.'} />
        ) : (
          <div className="space-y-2.5">
            {addresses.map((addr) => (
              <div key={addr.id || addr.label} className="bg-brand-light/30 border border-black/5 rounded-2xl p-3.5">
                <div className="flex items-start gap-2.5">
                  <span className="text-lg shrink-0 mt-0.5">📍</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-extrabold text-brand-dark flex items-center gap-1.5">
                      {addr.label || 'Address'}
                      {addr.is_default ? <span className="text-[10px] bg-gold-light text-gold px-1.5 py-0.5 rounded-full">Default</span> : null}
                    </div>
                    {addr.name || addr.phone_contact ? <div className="text-xs text-ink-soft mt-0.5">{[addr.name, addr.phone_contact].filter(Boolean).join(' · ')}</div> : null}
                    <div className="text-xs text-ink-faint mt-0.5">{[addr.line1, addr.landmark, addr.city].filter(Boolean).join(', ') || addr.notes || ''}</div>
                    {addr.id ? (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {!addr.is_default ? <button type="button" onClick={() => setDefault(addr.id)} className="text-[11px] font-bold bg-gold-light text-gold px-2.5 py-1 rounded-lg">Set default</button> : null}
                        <button type="button" onClick={() => setForm({ ...addr, lat: addr.lat != null ? addr.lat : '', lng: addr.lng != null ? addr.lng : '' })} className="text-[11px] font-bold bg-brand-light text-brand-dark px-2.5 py-1 rounded-lg">Edit</button>
                        <button type="button" onClick={() => remove(addr.id)} className="text-[11px] font-bold bg-red-50 text-red-600 px-2.5 py-1 rounded-lg">Delete</button>
                      </div>
                    ) : null}
                  </div>
                  {addr.lat != null && addr.lat !== '' ? (
                    <a href={`https://www.google.com/maps?q=${addr.lat},${addr.lng}`} target="_blank" rel="noreferrer" className="shrink-0 bg-brand-light rounded-lg px-2.5 py-1.5">🗺️</a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
